import { PushNotifications, type Channel } from '@capacitor/push-notifications';
import { ErrorWithBreadcrumb } from '~/errors';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Device, type DeviceInfo } from '@capacitor/device';

const pushTokenKey = 'pushNotification_Token';
const deviceIdKey = 'pushNotification_DeviceId';

async function set(key: string, value: string) {
  await SecureStoragePlugin.set({ key, value });
}

async function remove(key: string) {
  await SecureStoragePlugin.remove({ key });
}

async function get(key: string) {
  try {
    return (await SecureStoragePlugin.get({ key })).value;
  } catch {
    return null;
  }
}

export const pushNotificationService = {
  
  async getDeviceId() {
    return get(deviceIdKey);
  },

  async getPushToken() {
    return get(pushTokenKey);
  },

  async checkPushNotification() {
    if (!usePlatformStore().isMobile) {
      return;
    }

    let permissionStatus = await PushNotifications.checkPermissions();

    if (permissionStatus.receive === 'prompt') {
      permissionStatus = await PushNotifications.requestPermissions();
    }

    if (permissionStatus.receive !== 'granted') {
      return;
    }

    if (permissionStatus.receive === 'granted') {
      await pushNotificationService.registerUserDevice();
      await pushNotificationService.addListeners();
    }
  },

  async registerUserDevice() {
    
    if (await pushNotificationService.getDeviceId() && await pushNotificationService.getPushToken()) {
      return;
    }

    await PushNotifications.register();

    const registrationPromise = new Promise<void>((resolve, reject) => {
      PushNotifications.addListener('registration', async token => {
        await set(pushTokenKey, token.value);
        resolve();
      });

      set(deviceIdKey, crypto.randomUUID());

      PushNotifications.addListener('registrationError', async error => {
        reject(new ErrorWithBreadcrumb({
          category: BreadCrumbCategoryEnum.PushNotifications,
          message: 'Push notification registration failed',
          cause: error
        }));
      });
    });

    await registrationPromise;

    const deviceInfo: DeviceInfo = await Device.getInfo();

    const nickname = `${deviceInfo.manufacturer} ${deviceInfo.model}`;
    const deviceId = await pushNotificationService.getDeviceId();
    const pushToken = await pushNotificationService.getPushToken();

    if (!pushToken || !deviceId) {
      return;
    }

    let notificationPlatform: NotificationPlatform;

    if (usePlatformStore().isAndroid) {
      notificationPlatform = NotificationPlatform.FcmV1;
    } else if (usePlatformStore().isIos) {
      notificationPlatform = NotificationPlatform.Apns;
    } else {
      notificationPlatform = NotificationPlatform.FcmV1;
    }

    const notificationData: RegisterDeviceDTO = {
      DeviceId: deviceId,
      Platform: notificationPlatform,
      PushToken: pushToken,
      Name: nickname,
      FitterId: useUserConfigurationStore().config.FitterID
    };

    try {
      const { data } = await useApiFetch(versionedUrl('PushNotification/RegisterFitterDevice'), {
        method: 'POST',
        body: notificationData
      });

      if (data.value?.HasErrors) {
        throw new ErrorWithBreadcrumb({
          category: BreadCrumbCategoryEnum.PushNotifications,
          message: 'Could not register device'
        });
      }
    } catch {
      await remove(pushTokenKey);
      await remove(deviceIdKey);
    }
  },

  async unregisterPushNotification() {
    if (!usePlatformStore().isMobile) {
      return;
    }

    const deviceId = await pushNotificationService.getDeviceId();
    const pushToken = await pushNotificationService.getPushToken();

    if (!pushToken || !deviceId) {
      return;
    }

    const { data } = await useApiFetch(versionedUrl(`PushNotification/RemoveFitterDeviceByDeviceID?deviceID=${deviceId}`), {
      method: 'DELETE'
    });

    if (!data.value?.HasErrors) {
      await PushNotifications.unregister();
      await PushNotifications.removeAllListeners();
      await remove(pushTokenKey);
      await remove(deviceIdKey);
    }
  },

  async addListeners() {
    await PushNotifications.addListener('pushNotificationReceived', notification => {
      pushNotificationService.handlePushNotification(notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
      pushNotificationService.handlePushNotificationAction(notification.actionId, notification.notification.data);
    });

  },

  async handlePushNotification(notification: unknown) {
    // Implement your logic for processing notifications
    console.log('Notification received:', notification);
  },

  async handlePushNotificationAction(actionId: string, inputValue?: unknown) {
    // Implement your logic for handling notification actions

    console.log('Notification action performed:', actionId, inputValue);
  },

  //Maybe use this later?
  async createNotificationChannels() {
    try {
      const channels: Channel[] = [
        {
          id: 'general',
          name: 'General Notifications',
          description: 'Notifications for general updates',
          importance: 3,
          visibility: 1
        },
        {
          id: 'alerts',
          name: 'Alerts',
          description: 'Urgent alerts',
          importance: 5,
          visibility: 0
        },
        {
          id: 'messages',
          name: 'Messages',
          description: 'New messages',
          importance: 4,
          visibility: -1
        }
      ];

      for (const channel of channels) {
        await PushNotifications.createChannel(channel);
      }

    } catch (error) {
      throw new ErrorWithBreadcrumb({
        category: BreadCrumbCategoryEnum.PushNotifications,
        message: 'Could not create push channels',
        cause: error
      });
    }
  }
};
